import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import { FaChevronDown, FaChevronUp, FaEdit, FaEye } from "react-icons/fa";
import api from "../../services/api";
import { formataDataExibicao, formataDataHoraExibicao, tiposSessoesCampos } from "../../common/utils";
import ModalEditarMotivoCancelamento from "./ModalEditarMotivoCancelamento";

const formatarValorRelato = (valor) => {
    if (valor === "true") return "Sim";
    if (valor === "false") return "Não";
    if (valor && /^\d{4}-\d{2}-\d{2}$/.test(valor)) {
        const [ano, mes, dia] = valor.split("-");
        return `${dia}/${mes}/${ano}`;
    }
    return valor;
};

const renderizaCampos = (valores) => {
    if (!Array.isArray(valores) || valores.length === 0) return null;

    const grupos = {};
    valores.forEach(item => {
        const secao = item.formulario_campo__sessao || "0_sem_sessao";
        if (!grupos[secao]) grupos[secao] = [];
        grupos[secao].push(item);
    });

    return Object.entries(grupos).map(([secao, lista]) => {
        const visiveis = lista.filter(c => c.arquivo || (c.valor && c.valor.toString().trim() !== ""));
        if (visiveis.length === 0) return null;

        return (
            <div key={secao} className="row-list pb-0 mb-2">
                {secao !== "0_sem_sessao" && (
                    <h6><strong>{tiposSessoesCampos[secao] || secao}</strong></h6>
                )}
                <Row>
                    {visiveis.map(c => (
                        <Col xs={12} sm={6} key={c.id} className="mb-3">
                            {c.arquivo ? (
                                <Button variant="light" size="sm" href={c.arquivo} target="_blank">
                                    <FaEye className="me-1" /> Ver {c.formulario_campo__nome}
                                </Button>
                            ) : (
                                <>
                                    <strong>{c.formulario_campo__nome}:</strong>
                                    <div style={{ whiteSpace: "pre-wrap" }}>
                                        <p className="mb-0">
                                            {c.valor && /^\d{4}-\d{2}-\d{2}$/.test(c.valor)
                                                ? formataDataExibicao(c.valor)
                                                : formatarValorRelato(c.valor)}
                                        </p>
                                    </div>
                                </>
                            )}
                        </Col>
                    ))}
                </Row>
            </div>
        );
    });
};

const RelatoItem = ({ relato, podeEditarMotivo, onEditarMotivo }) => {
    const [aberto, setAberto] = useState(false);
    const temValores = Array.isArray(relato.valores_list) && relato.valores_list.length > 0;
    const tituloDefault = relato.tipo === "atendimento" ? "Registro da Etapa" : "Registro";

    return (
        <div className="row-list list-style-2 mb-2 pb-0 row-relato-etapa">
            <Row>
                <Col sm={12}>
                    <h6 className="relato-titulo">{relato.titulo || tituloDefault}</h6>
                    <p className="mb-1">
                        Data: <strong>{formataDataHoraExibicao(relato.data)}</strong>
                        {relato.dados_cadastrado_por && "nome" in relato.dados_cadastrado_por
                            ? ` por ${relato.dados_cadastrado_por.nome} ${relato.dados_cadastrado_por.sobrenome || ""}`
                            : ""}
                    </p>
                    {relato.motivo_cancelamento && (
                        <p className="mb-1">
                            <strong>Motivo do cancelamento:</strong> {relato.motivo_cancelamento}
                            {podeEditarMotivo && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 ms-2 align-baseline"
                                    title="Editar motivo do cancelamento"
                                    onClick={() => onEditarMotivo(relato)}
                                >
                                    <FaEdit /> Editar
                                </Button>
                            )}
                        </p>
                    )}
                </Col>
            </Row>
            {aberto && temValores && (
                <div className="relato-valores">
                    {renderizaCampos(relato.valores_list)}
                </div>
            )}
            {temValores && (
                <div
                    className="text-center show-hide-relato"
                    onClick={() => setAberto(prev => !prev)}
                    style={{ cursor: "pointer" }}
                >
                    {aberto ? (<>Ocultar <FaChevronUp /></>) : (<>Ver Informações <FaChevronDown /></>)}
                </div>
            )}
        </div>
    );
};

RelatoItem.propTypes = {
    relato: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        titulo: PropTypes.string,
        data: PropTypes.string,
        tipo: PropTypes.string,
        motivo_cancelamento: PropTypes.string,
        dados_cadastrado_por: PropTypes.object,
        valores_list: PropTypes.array,
    }).isRequired,
    podeEditarMotivo: PropTypes.bool,
    onEditarMotivo: PropTypes.func,
};

RelatoItem.defaultProps = {
    podeEditarMotivo: false,
    onEditarMotivo: () => {},
};

const ModalRelatosEtapaAtual = ({
    show,
    onHide,
    etapaId,
    etapaNome,
    atendimentoId,
    podeEditarMotivo,
    motivosCancelamento,
    onMotivoEditado,
}) => {
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState("");
    const [carregadoPara, setCarregadoPara] = useState(null);
    const [relatos, setRelatos] = useState([]);
    const [relatoMotivoEdicao, setRelatoMotivoEdicao] = useState(null);

    const aplicarMotivoEditado = (relatoId, novoMotivo) => {
        setRelatos(prev => prev.map(r => (
            r.id === relatoId ? { ...r, motivo_cancelamento: novoMotivo } : r
        )));

        // Propaga para quem abriu o modal, senão o histórico atrás dele fica com o
        // motivo antigo até a página ser recarregada.
        onMotivoEditado(relatoId, novoMotivo);
    };

    const carregar = useCallback(() => {
        if (!etapaId || !atendimentoId) return;
        setLoading(true);
        setErro("");
        api.get(`/atendimentos/relatos/?etapa_id=${etapaId}&atendimento_id=${atendimentoId}&limit=500`)
            .then(response => {
                setRelatos(response.data.results || []);
                setCarregadoPara(`${atendimentoId}-${etapaId}`);
                setLoading(false);
            })
            .catch(error => {
                console.error("Erro ao carregar relatos da etapa:", error);
                setErro("Não foi possível carregar os relatos. Tente novamente.");
                setLoading(false);
            });
    }, [etapaId, atendimentoId]);

    useEffect(() => {
        if (!show) return;
        const chaveAtual = `${atendimentoId}-${etapaId}`;
        if (carregadoPara !== chaveAtual && !loading) {
            carregar();
        }
    }, [show, etapaId, atendimentoId, carregadoPara, loading, carregar]);

    const relatosEtapa = relatos.filter(r => r.tipo !== "atendimento");
    const relatosFlutuantes = relatos.filter(r => r.tipo === "atendimento");
    const chaveAtual = `${atendimentoId}-${etapaId}`;
    const carregado = carregadoPara === chaveAtual && !loading && !erro;

    return (
        <Modal show={show} onHide={onHide} size="lg" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    Relatos da Etapa{etapaNome ? `: ${etapaNome}` : ""}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading && (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Carregando relatos...
                    </div>
                )}

                {!loading && erro && (
                    <Alert variant="danger">
                        {erro}
                        <div className="mt-2">
                            <Button size="sm" variant="outline-danger" onClick={carregar}>
                                Tentar novamente
                            </Button>
                        </div>
                    </Alert>
                )}

                {carregado && (
                    <>
                        <section className="mb-4">
                            <h5 className="mb-3">Registros da Etapa</h5>
                            {relatosEtapa.length > 0
                                ? relatosEtapa.map(r => (
                                    <RelatoItem
                                        key={r.id}
                                        relato={r}
                                        podeEditarMotivo={podeEditarMotivo}
                                        onEditarMotivo={setRelatoMotivoEdicao}
                                    />
                                ))
                                : <p className="text-muted">Nenhum registro cadastrado para esta etapa.</p>}
                        </section>
                        <section>
                            <h5 className="mb-3">Ocorrências (Relatos Flutuantes)</h5>
                            {relatosFlutuantes.length > 0
                                ? relatosFlutuantes.map(r => (
                                    <RelatoItem
                                        key={r.id}
                                        relato={r}
                                        podeEditarMotivo={podeEditarMotivo}
                                        onEditarMotivo={setRelatoMotivoEdicao}
                                    />
                                ))
                                : <p className="text-muted">Nenhuma ocorrência cadastrada para esta etapa.</p>}
                        </section>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Fechar</Button>
            </Modal.Footer>

            <ModalEditarMotivoCancelamento
                show={!!relatoMotivoEdicao}
                onHide={() => setRelatoMotivoEdicao(null)}
                relato={relatoMotivoEdicao}
                motivos={motivosCancelamento}
                onSalvo={aplicarMotivoEditado}
            />
        </Modal>
    );
};

ModalRelatosEtapaAtual.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    etapaId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    etapaNome: PropTypes.string,
    atendimentoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    podeEditarMotivo: PropTypes.bool,
    motivosCancelamento: PropTypes.arrayOf(PropTypes.string),
    onMotivoEditado: PropTypes.func,
};

ModalRelatosEtapaAtual.defaultProps = {
    podeEditarMotivo: false,
    motivosCancelamento: [],
    onMotivoEditado: () => {},
};

export default ModalRelatosEtapaAtual;
